import type { FC } from 'react';
import { useState } from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Popover,
  Switch,
  Typography,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import ArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import type { CreativeColumnKey } from '../../types/hypothesis';
import { CREATIVE_COLUMN_LABELS } from '../../types/hypothesis';

interface ColumnVisibilityMenuProps {
  columnOrder: CreativeColumnKey[];
  visibilityModel: Record<string, boolean>;
  onToggle: (key: CreativeColumnKey) => void;
  onMove: (key: CreativeColumnKey, direction: 'up' | 'down') => void;
  onReset: () => void;
}

const ColumnVisibilityMenu: FC<ColumnVisibilityMenuProps> = ({
  columnOrder,
  visibilityModel,
  onToggle,
  onMove,
  onReset,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => { setAnchorEl(e.currentTarget); }}
        sx={{ ml: 1 }}
      >
        <SettingsIcon fontSize="small" />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => { setAnchorEl(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, minWidth: 280 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Column visibility
          </Typography>
          <Divider sx={{ mb: 1 }} />
          {columnOrder.map((key, idx) => (
            <Box
              key={key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 0.25,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Switch
                  size="small"
                  checked={visibilityModel[key] ?? true}
                  onChange={() => { onToggle(key); }}
                />
                <Typography variant="body2">
                  {CREATIVE_COLUMN_LABELS[key]}
                </Typography>
              </Box>
              <Box>
                <IconButton
                  size="small"
                  disabled={idx === 0}
                  onClick={() => { onMove(key, 'up'); }}
                >
                  <ArrowUpIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={idx === columnOrder.length - 1}
                  onClick={() => { onMove(key, 'down'); }}
                >
                  <ArrowDownIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ))}
          <Divider sx={{ mt: 1, mb: 1 }} />
          <Button size="small" onClick={onReset}>
            Reset to defaults
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default ColumnVisibilityMenu;
